import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { Badge } from '@seen/ui'
import type { JSX } from 'react'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { RadioGroup } from '#src/components/core/radio-group/RadioGroup.tsx'
import { RadioGroupItem } from '#src/components/core/radio-group/RadioGroupItem.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useValidatedToolForm } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import {
  ValidatedToolInnerForm,
  type ValidatedToolInnerFormProps,
} from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import { ValidatedToolResponseCard } from '#src/components/validated-tool-form/shared/ValidatedToolResponseCard.tsx'
import { ValidatedToolSection } from '#src/components/validated-tool-form/shared/ValidatedToolSection.tsx'
import type { UseFormReturn } from '#src/hooks/useForm.ts'

export interface AD8FormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  response?: ValidatedToolResponse
  /* AI-staged answers to pre-populate an unsubmitted form. */
  prefillValuesByLinkId?: Record<string, string>
  /* No `renderFieldSuggestions`: this form renders its questions as a MATRIX —
   * one row of column headers (Yes/No/N/A) above bare, label-less radios — so
   * there is no per-option row for a suggestion's controls to anchor to.
   * `ValidatedToolInnerForm` absorbs the prop, so the router can go on handing
   * it to every form without it reaching the DOM. */
  restorationId?: string
}

const ad8AnswerEnum = z.enum(['yes', 'no', 'na']).nullable()

const schema = z.object({
  judgment: ad8AnswerEnum,
  hobbies: ad8AnswerEnum,
  repeats: ad8AnswerEnum,
  'learning-tools': ad8AnswerEnum,
  'month-year': ad8AnswerEnum,
  finances: ad8AnswerEnum,
  appointments: ad8AnswerEnum,
  'daily-problems': ad8AnswerEnum,
  summary: z.string().nullable(),
})

const validatedTool = new ValidatedTool(questionnaires.ad8Questionnaire)

const AD8_QUESTIONS: {
  linkId: keyof z.infer<typeof schema>
  number: number
}[] = [
  { linkId: 'judgment', number: 1 },
  { linkId: 'hobbies', number: 2 },
  { linkId: 'repeats', number: 3 },
  { linkId: 'learning-tools', number: 4 },
  { linkId: 'month-year', number: 5 },
  { linkId: 'finances', number: 6 },
  { linkId: 'appointments', number: 7 },
  { linkId: 'daily-problems', number: 8 },
]

export function AD8Form({
  response,
  prefillValuesByLinkId,
  restorationId,
  ...delegated
}: AD8FormProps): JSX.Element {
  const form = useValidatedToolForm({
    schema,
    validatedTool,
    response,
    prefillValuesByLinkId,
    restorationId,
  })

  return (
    <ValidatedToolInnerForm
      validatedTool={validatedTool}
      subtitle={validatedTool.getItemText('instructions')}
      form={form}
      response={response}
      {...delegated}
    >
      {!!response && (
        <ValidatedToolResponseCard response={response}>
          <Score
            form={form}
            validatedTool={validatedTool}
            response={response}
          />
          <FormField
            control={form.control}
            name="summary"
            render={({ field: { value, ...rest } }) =>
              !value ? null : (
                <RichTextEditor
                  defaultValue={value}
                  variant="unstyled"
                  placeholder="Add summary…"
                  contentClassName="max-w-(--container-prose)"
                  {...rest}
                  disabled={false}
                  editable={false}
                />
              )
            }
          />
        </ValidatedToolResponseCard>
      )}
      <ValidatedToolSection title="AD8 Screening">
        <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-4 gap-y-0 border-b pb-2">
          <div />
          <div className="w-20 text-center text-sm font-semibold">
            Yes,
            <br />A change
          </div>
          <div className="w-20 text-center text-sm font-semibold">
            No,
            <br />
            No change
          </div>
          <div className="w-20 text-center text-sm font-semibold">
            N/A,
            <br />
            Don't know
          </div>
        </div>
        {AD8_QUESTIONS.map(({ linkId, number }) => (
          <FormField
            key={linkId}
            control={form.control}
            name={linkId}
            render={({ field: { value, ...rest }, fieldState }) => (
              <div
                className={`grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-4 border-b py-2 ${fieldState.error ? 'rounded px-2 ring-1 ring-destructive' : ''}`}
              >
                <div className="text-sm">
                  <span className="font-semibold">{number}.</span>{' '}
                  {validatedTool.getItemText(linkId)}
                </div>
                <RadioGroup
                  className="contents"
                  value={value ?? undefined}
                  {...rest}
                >
                  <label className="flex w-20 cursor-pointer items-center justify-center py-2">
                    <RadioGroupItem value="yes" />
                  </label>
                  <label className="flex w-20 cursor-pointer items-center justify-center py-2">
                    <RadioGroupItem value="no" />
                  </label>
                  <label className="flex w-20 cursor-pointer items-center justify-center py-2">
                    <RadioGroupItem value="na" />
                  </label>
                </RadioGroup>
              </div>
            )}
          />
        ))}
      </ValidatedToolSection>
      <Score form={form} validatedTool={validatedTool} response={response} />
      <ValidatedToolSection title="Summary">
        <FormField
          control={form.control}
          name="summary"
          render={({ field: { value, ...rest } }) => (
            <RichTextEditor
              defaultValue={value ?? ''}
              placeholder="Add summary…"
              contentClassName="max-w-(--container-prose)"
              {...rest}
            />
          )}
        />
      </ValidatedToolSection>
      {!response && (
        <div>
          <FormSubmitButton control={form.control}>Submit</FormSubmitButton>
        </div>
      )}
    </ValidatedToolInnerForm>
  )
}

function Score({
  form,
  validatedTool,
  response,
}: {
  form: UseFormReturn<any>
  validatedTool: ValidatedTool
  response?: ValidatedToolResponse
}): JSX.Element {
  const valuesByLinkId = form.watch()
  const result = response
    ? response
    : ValidatedToolResponse.buildSafe(validatedTool, { valuesByLinkId })
  const { color, label } = match(result.score)
    .when(
      (s) => s <= 1,
      () => ({ color: 'green' as const, label: 'Normal Cognition' })
    )
    .when(
      (s) => s >= 2,
      () => ({
        color: 'red' as const,
        label: 'Cognitive Impairment Likely',
      })
    )
    .otherwise(() => ({ color: 'gray' as const, label: 'Not Scored' }))

  return (
    <div className="flex items-center gap-2">
      <h2>
        Score: <b>{result.score}</b>
      </h2>
      <Badge color={color} className="mt-px">
        {label}
      </Badge>
    </div>
  )
}
